/**
 * Clarid Agent: Ask Clarid (CLARID-SPEC-AGENT-001 §4; ASK_CLARID_PILOT.md).
 *
 * A chat about the active bank. Each answer shows the checks Clarid ran to reach it and whether every
 * figure was confirmed against Clarid's records. Files can be dropped onto the conversation to be
 * identified or verified; they are read for that answer only and not stored.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import {
  SparklesIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '../components/ui';
import ActiveBankNotice from '../components/ActiveBankNotice';
import { useTeamId } from '../hooks/useTeamId';
import { useActiveTeamStore } from '../store/activeTeamStore';
import { agentApi, fileSha256, type AgentCardRef, type AgentStep, type NumbersCheck } from '../lib/agent';
import AgentCards from '../components/agent/AgentCards';
import AgentHome from '../components/agent/AgentHome';
import { AgentFilesContext } from '../components/agent/agentFiles';

interface ChatEntry {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  steps?: AgentStep[];
  cards?: AgentCardRef[];
  numbersCheck?: NumbersCheck;
  attachments?: string[];
  error?: boolean;
}

const SUGGESTIONS = [
  'Is our data ready for this year’s fair lending review?',
  'What are our fair lending risks this year?',
  'Find any data problems Clarid can fix.',
  'Get the FLD report ready with the evidence appendix.',
  'Prepare the file review for our Focal Points.',
  'What’s waiting for my sign-off?',
];

const MAX_FILES = 5;

export default function ClaridAgentPage() {
  const { teamId } = useTeamId();
  const setActiveTeamId = useActiveTeamStore((s) => s.setActiveTeamId);
  // A link from Copilot or Claude (?team=…&conversation=…): switch to that bank, then open the conversation
  // where its proposals wait for approval. Read once; the server checks access to both.
  const pendingLink = useRef<{ team: string | null; conversation: string } | null>(null);
  if (pendingLink.current === null && typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search);
    const conversation = q.get('conversation');
    pendingLink.current = conversation ? { team: q.get('team'), conversation } : { team: null, conversation: '' };
  }
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  // Files attached this session, by SHA-256, so a Load card sends the exact file the agent saw.
  const sessionFiles = useRef(new Map<string, File>());
  const filesContext = useMemo(() => ({ get: (sha: string) => sessionFiles.current.get(sha) }), []);

  // A new bank is a new conversation.
  useEffect(() => {
    setConversationId(null);
    setEntries([]);
    setFiles([]);
  }, [teamId]);

  const conversations = useQuery({
    queryKey: ['agent-conversations', teamId],
    queryFn: () => agentApi.conversations(teamId!).then((r) => r.data),
    enabled: !!teamId,
  });
  const unavailable = conversations.data && !conversations.data.available;

  const send = useMutation({
    mutationFn: (vars: { message: string; files: File[] }) =>
      agentApi.send(teamId!, { message: vars.message, conversationId, files: vars.files }).then((r) => r.data),
    onSuccess: (turn) => {
      setConversationId(turn.conversationId);
      setEntries((prev) => [...prev, { id: turn.messageId, role: 'assistant', content: turn.answer, steps: turn.steps, cards: turn.cards, numbersCheck: turn.numbersCheck }]);
      queryClient.invalidateQueries({ queryKey: ['agent-conversations', teamId] });
      queryClient.invalidateQueries({ queryKey: ['agent-home', teamId] });
    },
    onError: (err: any) => {
      setEntries((prev) => [...prev, {
        id: `err-${Date.now()}`, role: 'assistant', error: true,
        content: err?.response?.data?.error ?? 'Ask Clarid could not answer just now. Please try again.',
      }]);
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries.length, send.isPending]);

  const submit = (text?: string) => {
    const message = (text ?? draft).trim();
    if ((!message && files.length === 0) || send.isPending || !teamId) return;
    setEntries((prev) => [...prev, {
      id: `u-${Date.now()}`, role: 'user', content: message || 'What are these files?', attachments: files.map((f) => f.name),
    }]);
    for (const f of files) fileSha256(f).then((sha) => sessionFiles.current.set(sha, f)).catch(() => undefined);
    send.mutate({ message, files });
    setDraft('');
    setFiles([]);
  };

  const openConversation = async (id: string) => {
    if (!teamId) return;
    const { data } = await agentApi.conversation(teamId, id);
    setConversationId(id);
    setEntries(data.conversation.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.role === 'user' ? m.content.replace(/\n\n\[Attached: [^\]]*\]$/, '') : m.content,
      steps: m.metadata?.steps,
      cards: m.metadata?.cards,
      numbersCheck: m.metadata?.numbersCheck,
      attachments: m.metadata?.attachments?.map((a) => a.name),
    })));
  };

  useEffect(() => {
    const link = pendingLink.current;
    if (!link?.conversation || !teamId) return;
    if (link.team && link.team !== teamId) {
      setActiveTeamId(link.team);
      return;
    }
    pendingLink.current = { team: null, conversation: '' };
    const q = new URLSearchParams(window.location.search);
    q.delete('team');
    q.delete('conversation');
    window.history.replaceState(null, '', `${window.location.pathname}${q.toString() ? `?${q}` : ''}`);
    openConversation(link.conversation).catch(() => undefined);
    // openConversation is recreated each render; the link is consumed once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, setActiveTeamId]);

  const newConversation = () => {
    setConversationId(null);
    setEntries([]);
    setFiles([]);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: MAX_FILES,
    maxSize: 50 * 1024 * 1024,
    onDrop: (accepted) => setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES)),
  });

  return (
    <div>
      <PageHeader
        title="Clarid Agent"
        description="Ask Clarid about this bank's data, its fair lending results, or a file. Every figure in an answer is checked against Clarid's records."
      />
      <ActiveBankNotice className="mb-6" />

      {unavailable && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Ask Clarid is not available on this server yet.
        </div>
      )}

      <AgentFilesContext.Provider value={filesContext}>
      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Conversations */}
        <aside className="hidden lg:block">
          <button
            type="button"
            onClick={newConversation}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-900 hover:bg-gray-50"
          >
            <PlusIcon className="h-4 w-4" /> New conversation
          </button>
          <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-gray-400">Recent</p>
          <ul className="space-y-1">
            {(conversations.data?.conversations ?? []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => openConversation(c.id)}
                  className={clsx(
                    'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    c.id === conversationId ? 'bg-ocean-50 text-ocean-800' : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  <ChatBubbleLeftRightIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="line-clamp-2">{c.title}</span>
                </button>
              </li>
            ))}
            {conversations.data?.conversations.length === 0 && <li className="px-2 text-sm text-gray-400">No conversations yet.</li>}
          </ul>
        </aside>

        {/* Chat */}
        <section
          {...getRootProps()}
          className="relative flex h-[calc(100vh-16rem)] min-h-[480px] flex-col rounded-xl border border-gray-200 bg-white shadow-sm"
        >
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-ocean-500 bg-ocean-50/90 text-ocean-800">
              <DocumentMagnifyingGlassIcon className="mb-2 h-10 w-10" />
              <p className="font-medium">Drop to attach</p>
              <p className="text-sm">Clarid will identify each file, or verify a report it produced.</p>
            </div>
          )}

          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
            {entries.length === 0 && !send.isPending ? (
              <EmptyState teamId={teamId} onPick={submit} onAttach={open} onOpenConversation={openConversation} />
            ) : (
              entries.map((e) => (e.role === 'user' ? <UserBubble key={e.id} entry={e} /> : <AnswerCard key={e.id} entry={e} teamId={teamId} />))
            )}
            {send.isPending && <Working />}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-gray-200 px-4 py-3">
            <AnimatePresence>
              {files.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                      <PaperClipIcon className="h-3.5 w-3.5" />
                      {f.name}
                      <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                        <XMarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
                      </button>
                    </span>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={open}
                title="Attach files"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <PaperClipIcon className="h-5 w-5" />
              </button>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                rows={1}
                placeholder="Ask about this bank's data or its fair lending results, or attach a SOW and ask Clarid to plan it…"
                className="max-h-40 min-h-[40px] flex-1 resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-1 focus:ring-ocean-500"
              />
              <button
                type="button"
                onClick={() => submit()}
                disabled={send.isPending || !teamId || (!draft.trim() && files.length === 0)}
                className="rounded-lg bg-ocean-600 p-2 text-white hover:bg-ocean-700 disabled:opacity-50"
                aria-label="Send"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Ask Clarid never changes data on its own: every action waits for your approval and is recorded. Answers are not legal advice.
            </p>
          </div>
        </section>
      </div>
      </AgentFilesContext.Provider>
    </div>
  );
}

function EmptyState({
  teamId, onPick, onAttach, onOpenConversation,
}: {
  teamId?: string; onPick: (text: string) => void; onAttach: () => void; onOpenConversation: (id: string) => void;
}) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center pt-4 text-center">
      <div className="mb-4 rounded-full bg-ocean-50 p-3">
        <SparklesIcon className="h-7 w-7 text-ocean-600" />
      </div>
      <h2 className="font-display text-xl font-semibold text-navy-900">Ask Clarid</h2>
      <p className="mt-1 text-sm text-gray-500">
        Answers come only from this bank's records in Clarid, and every figure is checked before you see it. Clarid can also prepare actions, such as a data fix or an analysis run, for you to approve.
      </p>
      {teamId && (
        <div className="mt-6 w-full">
          <AgentHome teamId={teamId} onAsk={onPick} onOpenConversation={onOpenConversation} />
        </div>
      )}
      <p className="mt-6 w-full max-w-3xl text-left text-xs font-medium uppercase tracking-wide text-gray-400">Or ask</p>
      <div className="mt-2 grid w-full max-w-3xl gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className="rounded-lg border border-gray-200 px-3 py-2.5 text-left text-sm text-gray-700 hover:border-ocean-300 hover:bg-ocean-50"
          >
            {s}
          </button>
        ))}
      </div>
      <button type="button" onClick={onAttach} className="mt-4 text-sm font-medium text-ocean-700 hover:text-ocean-800">
        Or drop files here: a SOW to plan an engagement, an HMDA LAR to load, other data files to identify, or a Clarid report to verify.
      </button>
    </div>
  );
}

function UserBubble({ entry }: { entry: ChatEntry }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
      <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-ocean-600 px-4 py-2.5 text-sm text-white">
        <p className="whitespace-pre-wrap">{entry.content}</p>
        {entry.attachments?.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {entry.attachments.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-xs">
                <PaperClipIcon className="h-3 w-3" /> {a}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

const CHECK_TEXT: Record<NumbersCheck, { text: string; tone: 'ok' | 'warn' | 'none' }> = {
  passed: { text: "Every figure checked against Clarid's records", tone: 'ok' },
  passed_on_retry: { text: "Every figure checked against Clarid's records", tone: 'ok' },
  no_figures: { text: "Answered from Clarid's records", tone: 'none' },
  fallback: { text: "Shown word for word from Clarid's records, because a figure in the draft could not be confirmed", tone: 'warn' },
};

function AnswerCard({ entry, teamId }: { entry: ChatEntry; teamId?: string }) {
  const check = entry.numbersCheck ? CHECK_TEXT[entry.numbersCheck] : null;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
      <div className="mt-1 h-7 w-7 shrink-0 rounded-full bg-ocean-50 p-1.5">
        <SparklesIcon className="h-4 w-4 text-ocean-600" />
      </div>
      <div className={clsx('min-w-0 max-w-[85%] flex-1', entry.error && 'text-red-700')}>
        {entry.steps?.length ? (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {entry.steps.map((s, i) => (
              <span
                key={`${s.tool}-${i}`}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs',
                  s.refused ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600',
                )}
              >
                {s.refused ? <ExclamationTriangleIcon className="h-3.5 w-3.5" /> : <CheckCircleIcon className="h-3.5 w-3.5 text-ocean-600" />}
                {s.label}
              </span>
            ))}
          </div>
        ) : null}
        <div className="space-y-2 text-sm leading-relaxed text-gray-800">
          <FormattedAnswer text={entry.content} />
        </div>
        {teamId && entry.cards?.length ? <AgentCards teamId={teamId} refs={entry.cards} /> : null}
        {check && check.tone !== 'none' && (
          <p className={clsx('mt-2 inline-flex items-center gap-1 text-xs', check.tone === 'ok' ? 'text-emerald-700' : 'text-amber-700')}>
            {check.tone === 'ok' ? <ShieldCheckIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
            {check.text}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function Working() {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-500">
      <div className="h-7 w-7 shrink-0 rounded-full bg-ocean-50 p-1.5">
        <SparklesIcon className="h-4 w-4 animate-pulse text-ocean-600" />
      </div>
      Checking Clarid's records
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: `${i * 150}ms` }} />
        ))}
      </span>
    </div>
  );
}

/** Paragraphs, bullet lists and **bold**, rendered as elements (never as HTML). */
function FormattedAnswer({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`l${blocks.length}`} className="list-disc space-y-1 pl-5">
          {list.map((li, i) => <li key={i}>{inline(li)}</li>)}
        </ul>,
      );
      list = [];
    }
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const bullet = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (bullet) list.push(bullet[1]);
    else {
      flush();
      if (line) blocks.push(<p key={`p${blocks.length}`}>{inline(line)}</p>);
    }
  }
  flush();
  return <>{blocks}</>;
}

function inline(s: string): ReactNode {
  return s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>,
  );
}
